const pulse = 'animate-pulse rounded-2xl bg-muted'

export function Skeleton({ className = '', ...props }) {
  return <div className={`${pulse} ${className}`} {...props} />
}

/** Single or multiple lines of text placeholder */
export function SkeletonText({ lines = 1, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className={`h-4 ${i === lines - 1 && lines > 1 ? 'w-4/5' : 'w-full'}`}
        />
      ))}
    </div>
  )
}

export function SkeletonCircle({ className = 'h-10 w-10' }) {
  return <div className={`${pulse} ${className}`} />
}

/** Stat / card block */
export function SkeletonCard({ className = '' }) {
  return (
    <div
      className={`rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)] ${className}`}
      aria-busy="true"
      aria-hidden="true"
    >
      <Skeleton className="mb-3 h-3 w-24" />
      <Skeleton className="h-8 w-16" />
    </div>
  )
}

/** Organization detail / edit header + two-column body */
export function SkeletonDetailLayout() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading organization">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="space-y-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-9 w-64 max-w-full" />
          <Skeleton className="h-4 w-48 max-w-full" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <Skeleton className="h-5 w-40" />
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
        <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="mb-4 h-5 w-48" />
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-24" />
          ))}
        </div>
      </div>
    </div>
  )
}

/** Stacked form sections placeholder */
export function SkeletonFormSections() {
  return (
    <div className="space-y-8" aria-busy="true" aria-label="Loading form">
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="mb-6 h-6 w-48" />
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="mb-6 h-6 w-56" />
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
    </div>
  )
}

/** Detail `<dl>` grid placeholder */
export function SkeletonDetailFields({ fields = 6 }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2" aria-busy="true" aria-label="Loading details">
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-5 w-full max-w-xs" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonTable({ rows = 6, columns = 6 }) {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-border bg-card"
      aria-busy="true"
      aria-label="Loading table"
    >
      <div className="border-b border-border bg-muted/50 px-4 py-3">
        <div className="flex gap-4">
          {Array.from({ length: columns }, (_, i) => (
            <Skeleton key={i} className="h-3 flex-1" />
          ))}
        </div>
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="flex gap-4 px-4 py-3.5">
            {Array.from({ length: columns }, (_, c) => (
              <Skeleton key={c} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Mobile list rows (e.g. payment gateways cards) */
export function SkeletonStackedCards({ count = 3, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`} aria-busy="true" aria-label="Loading list">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4"
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <SkeletonCircle className="h-9 w-9 shrink-0" />
            <SkeletonCircle className="h-10 w-10 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-32 max-w-full" />
              <Skeleton className="h-3 w-24 max-w-full" />
            </div>
          </div>
          <Skeleton className="h-8 w-14 shrink-0 rounded-lg" />
        </div>
      ))}
    </div>
  )
}
